import { SupportPermissions } from "@redotech/account-common/permissions";
import {
  useLazyContext,
  useRequiredContext,
} from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import {
  isVoiceEnabledPhoneNumber,
  VoiceEnabledPhoneNumber,
} from "@redotech/redo-common-model/phone/phone-number";
import { CustomerJson } from "@redotech/redo-customer-common/customer";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { getCustomer } from "@redotech/redo-merchant-app-common/client/customer";
import { MerchantAppTopic } from "@redotech/redo-merchant-app-common/events/merchant-app-event-server";
import { MerchantAppEventServerContext } from "@redotech/redo-merchant-app-common/events/merchant-app-event-server-provider";
import { getUserAvatarUrl } from "@redotech/redo-merchant-app-common/get-avatar-url";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import {
  FrontendTeamNoticeEvent,
  TeamNoticeEventType,
} from "@redotech/redo-notifications-common/team-notifications";
import {
  Notification,
  NotificationType,
} from "@redotech/redo-support-common/notification";
import {
  CallFlow,
  supportAgentCallingCustomerFormat,
  supportAgentResponseCallFormat,
} from "@redotech/redo-support-common/support/voice/voice-client-state";
import {
  Call,
  CallDirection,
  CallState,
  VoiceAgent,
} from "@redotech/redo-support-common/support/voice/voice-types";
import { VoiceProvider } from "@redotech/redo-support-settings-common/voice-settings";
import { Flex } from "@redotech/redo-web/flex";
import { PhoneWidget } from "@redotech/redo-web/voice/phone-widget";
import {
  isPhoneWidgetHiddenByUser,
  usePhoneWidget,
} from "@redotech/redo-web/voice/shared/phone-widget-context";
import {
  VoiceContext,
  VoiceState,
} from "@redotech/redo-web/voice/voice-context";
import { convertArbitraryPhoneNumberToE164 } from "@redotech/util/phone-number";
import { assertNever } from "@redotech/util/type";
import { Audio } from "@telnyx/react-client";
import { TelnyxRTC } from "@telnyx/webrtc";
import { Call as TelnyxCall } from "@telnyx/webrtc/lib/src/Modules/Verto/webrtc/Call";
import jwtDecode from "jwt-decode";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PhoneNumberContext } from "../services/phone-number-service";
import { UserCallAvailabilityContext } from "../services/user-call-availability";
import { AircallWidget } from "./aircall-widget";
import * as voiceWidgetCss from "./voice-widget.module.css";

const LOCAL_STORAGE_KEY = "telnyx-voice-token";

export const VoiceWidget = memo(function VoiceWidget() {
  const team = useRequiredContext(TeamContext);
  const [phoneNumber] = useLazyContext(PhoneNumberContext);
  const { setVoiceAvailable, show } = usePhoneWidget();

  const hasSupportPermission = useHasPermission(
    SupportPermissions.VIEW_SUPPORT,
  );

  const voiceProvider =
    team.settings.support?.voice?.provider ?? VoiceProvider.TELNYX;

  const getVoiceAvailability = (): boolean => {
    switch (voiceProvider) {
      case VoiceProvider.AIRCALL:
        return team.settings.support?.voice?.enabled ?? false;
      case VoiceProvider.TELNYX:
        return !!(
          phoneNumber.value &&
          isVoiceEnabledPhoneNumber(phoneNumber.value) &&
          team.settings.support?.voice?.enabled &&
          team.settings.support?.billing?.voice?.subscriptionActive
        );
    }
    return assertNever(voiceProvider);
  };

  const isVoiceAvailable = getVoiceAvailability();

  useEffect(() => {
    setVoiceAvailable(isVoiceAvailable);
    if (isVoiceAvailable && !isPhoneWidgetHiddenByUser()) {
      show();
    }
  }, [isVoiceAvailable, setVoiceAvailable, show]);

  if (!isVoiceAvailable || !hasSupportPermission) {
    return null;
  }

  const renderVoiceWidget = () => {
    switch (voiceProvider) {
      case VoiceProvider.AIRCALL:
        return <AircallInternal />;
      case VoiceProvider.TELNYX:
        if (
          !phoneNumber.value ||
          !isVoiceEnabledPhoneNumber(phoneNumber.value)
        ) {
          return null;
        }
        return <PhoneInternal phoneNumber={phoneNumber.value} />;
    }
    return assertNever(voiceProvider);
  };

  return renderVoiceWidget();
});

const AircallInternal = memo(function AircallInternal() {
  const { show } = usePhoneWidget();
  const userCallAvailability = useRequiredContext(UserCallAvailabilityContext);
  const merchantClient = useRequiredContext(RedoMerchantClientContext);
  const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);

  const createAircallCall = useCallback(
    async (
      callId: number,
      direction: CallDirection,
      phoneNumber: string,
    ): Promise<Call> => {
      const fetchedCustomer =
        await merchantRpcClient.getOrCreateCustomerFromPhoneNumber({
          number: phoneNumber,
        });
      const customer = await getCustomer(merchantClient, {
        id: fetchedCustomer._id,
      });

      return {
        state: CallState.ACTIVE,
        callControlId: `aircall-${callId}`,
        direction,
        startTime: new Date(),
        remoteCallerNumber: phoneNumber,
        agents: [],
        customer,
      };
    },
    [merchantClient, merchantRpcClient],
  );

  const handleIncomingCall = useCallback(
    async (data: { from: string; to: string; callId: number }) => {
      show();
      const call = await createAircallCall(
        data.callId,
        CallDirection.INBOUND,
        data.from,
      );
      userCallAvailability.setCurrentCall(call);
    },
    [show, createAircallCall, userCallAvailability],
  );

  const handleOutgoingCall = useCallback(
    async (data: { from: string; to: string; callId: number }) => {
      const call = await createAircallCall(
        data.callId,
        CallDirection.OUTBOUND,
        data.to,
      );
      userCallAvailability.setCurrentCall(call);
    },
    [createAircallCall, userCallAvailability],
  );

  const handleCallEnded = useCallback(() => {
    userCallAvailability.setCurrentCall(undefined);
  }, [userCallAvailability]);

  return (
    <AircallWidget
      onCallEnded={handleCallEnded}
      onIncomingCall={handleIncomingCall}
      onOutgoingCall={handleOutgoingCall}
    />
  );
});

const PhoneInternal = memo(function PhoneInternal({
  phoneNumber,
}: {
  phoneNumber: VoiceEnabledPhoneNumber;
}) {
  const userCallAvailability = useRequiredContext(UserCallAvailabilityContext);
  const eventServer = useRequiredContext(MerchantAppEventServerContext);
  const merchantClient = useRequiredContext(RedoMerchantClientContext);
  const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const team = useRequiredContext(TeamContext);
  const user = useRequiredContext(UserContext);

  const telnyxClientRef = useRef<TelnyxRTC | null>(null);

  const [call, setCall] = useState<Call | undefined>(undefined);
  const [muted, setMuted] = useState(false);
  const [deafened, setDeafened] = useState(false);
  const [onHold, setOnHold] = useState(false);
  const [recording, setRecording] = useState(false);
  const { isVisible, setVoiceAvailable, show } = usePhoneWidget();
  const ringTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isValidTelnyxCall = useHandler((telnyxCall?: TelnyxCall): boolean => {
    if (!telnyxCall) {
      return false;
    }
    return !(
      !telnyxCall.state ||
      telnyxCall.state === "hangup" ||
      telnyxCall.state === "destroy"
    );
  });

  const getTelnyxToken = useHandler(async () => {
    const existingToken = localStorage.getItem(LOCAL_STORAGE_KEY);

    if (existingToken) {
      const decodedToken = jwtDecode(existingToken) as { exp: number };
      const tokenHasExpired = decodedToken.exp * 1000 < Date.now();

      if (!tokenHasExpired) {
        return existingToken;
      }
    }

    const { token } = await merchantRpcClient.getTelnyxTelephonyToken({});
    localStorage.setItem(LOCAL_STORAGE_KEY, token);
    return token;
  });

  const detachListeners = useCallback((client: TelnyxRTC) => {
    if (client) {
      client.off("telnyx.error");
      client.off("telnyx.ready");
      client.off("telnyx.socket.close");
      client.off("telnyx.socket.error");
      client.off("telnyx.socket.open");
    }
  }, []);

  const handleClientError = useCallback(
    (client: TelnyxRTC) => {
      client.disconnect().catch(() => {});
      detachListeners(client);
      telnyxClientRef.current = null;
    },
    [detachListeners],
  );

  const attachListeners = useCallback(
    (client: TelnyxRTC, onReady?: (client: TelnyxRTC) => void) => {
      client.on("telnyx.ready", () => {
        if (onReady) {
          onReady(client);
        }
      });

      client.on("telnyx.error", () => handleClientError(client));
      client.on("telnyx.socket.error", () => handleClientError(client));
      client.on("telnyx.socket.close", () => handleClientError(client));
    },
    [handleClientError],
  );

  const agent: VoiceAgent = useMemo(() => {
    return {
      name: user.name,
      imageUrl: getUserAvatarUrl({ email: user.email, userId: user._id }),
    };
  }, [user]);

  const createTelnyxClient = useCallback(
    async (onReady?: (client: TelnyxRTC) => void) => {
      try {
        if (telnyxClientRef.current) {
          await telnyxClientRef.current.disconnect();
          detachListeners(telnyxClientRef.current);
        }

        const token = await getTelnyxToken();
        const session = new TelnyxRTC({ login_token: token });

        attachListeners(session, onReady);

        await session.connect();
        telnyxClientRef.current = session;
      } catch (error) {
        console.error("Error creating Telnyx client:", error);
      }
    },
    [getTelnyxToken, attachListeners, detachListeners],
  );

  const performClientAction = useCallback(
    async (onReady: (client: TelnyxRTC) => void) => {
      try {
        if (telnyxClientRef.current && telnyxClientRef.current.connected) {
          onReady(telnyxClientRef.current);
        } else {
          await createTelnyxClient(onReady);
        }
      } catch (error) {
        console.error("Error connecting Telnyx client:", error);
      }
    },
    [createTelnyxClient],
  );

  useEffect(() => {
    if (!userCallAvailability) {
      setVoiceAvailable(false);
    }
  }, [userCallAvailability, setVoiceAvailable]);

  useEffect(() => {
    userCallAvailability.setCurrentCall(call);
  }, [call, userCallAvailability]);

  const answerCall = useHandler(async () => {
    if (!call) {
      return;
    }

    if (ringTimeoutRef.current) {
      clearTimeout(ringTimeoutRef.current);
      ringTimeoutRef.current = null;
    }

    const outboundState = supportAgentResponseCallFormat.write({
      type: CallFlow.CUSTOMER_CALLING_SUPPORT_AGENT,
      teamId: team._id,
      billingGroupId: phoneNumber.voiceMetadata.billingGroupId,
      callRecordId: "", // Gets filled in when created
      customerId: call.customer._id.toString(),
      customerNumber: call.remoteCallerNumber,
      customerCallId: call.callControlId,
      supportAgentUserId: user._id,
    });

    await performClientAction((client) => {
      const telnyxCall = client.newCall({
        destinationNumber: call.remoteCallerNumber,
        callerNumber: phoneNumber.number,
        callerName: team.name,
        audio: true,
        video: false,
        clientState: outboundState,
      });

      setCall((currentCall) =>
        currentCall
          ? { ...currentCall, state: CallState.CONNECTING, telnyxCall }
          : currentCall,
      );
    });
  });

  const performCall = useHandler(async (number: string) => {
    const e164Number = convertArbitraryPhoneNumberToE164(number);
    if (!e164Number) {
      return;
    }

    const fetchedCustomer =
      await merchantRpcClient.getOrCreateCustomerFromPhoneNumber({
        number: e164Number,
      });
    const customer = await getCustomer(merchantClient, {
      id: fetchedCustomer._id,
    });

    // Create temporary ID that will be updated when we get the webhook response
    const temporaryCallId = `outbound-${Date.now()}-${e164Number.slice(-4)}`;

    const outboundState = supportAgentCallingCustomerFormat.write({
      type: CallFlow.SUPPORT_AGENT_CALLING_CUSTOMER,
      teamId: team._id,
      supportAgentUserId: user._id,
      billingGroupId: phoneNumber.voiceMetadata.billingGroupId,
      isCustomerLeg: false,
      callRecordId: "", // Gets filled in when created
      customerId: fetchedCustomer._id,
      customerNumber: e164Number,
    });

    await performClientAction((client) => {
      const telnyxCall = client.newCall({
        destinationNumber: e164Number,
        callerNumber: phoneNumber.number,
        callerName: team.name,
        audio: true,
        video: false,
        clientState: outboundState,
      });

      const newCall = initializeCall(
        customer,
        e164Number,
        temporaryCallId,
        CallDirection.OUTBOUND,
        CallState.RINGING,
        telnyxCall,
      );
      setCall(newCall);
    });
  });

  const rejectCall = useHandler(() => {
    if (ringTimeoutRef.current) {
      clearTimeout(ringTimeoutRef.current);
      ringTimeoutRef.current = null;
    }
    setCall(undefined);
  });

  const endCall = useHandler(() => {
    if (ringTimeoutRef.current) {
      clearTimeout(ringTimeoutRef.current);
      ringTimeoutRef.current = null;
    }
    if (isValidTelnyxCall(call?.telnyxCall)) {
      call?.telnyxCall?.hangup();
    }
    if (call) {
      const updatedCall = {
        ...call,
        state: CallState.HANGUP,
        telnyxCall: undefined,
      };
      setCall(updatedCall);

      setTimeout(() => {
        setMuted(false);
        setDeafened(false);
        setOnHold(false);
        setRecording(false);
        setCall(undefined);
      }, 2000);
    }
  });

  const toggleMute = useHandler(async () => {
    if (isValidTelnyxCall(call?.telnyxCall)) {
      call?.telnyxCall?.toggleAudioMute();
      setMuted((prev) => !prev);
    }
  });

  const toggleDeafen = useHandler(async () => {
    if (isValidTelnyxCall(call?.telnyxCall)) {
      call?.telnyxCall?.toggleDeaf();
      setDeafened((prev) => !prev);
    }
  });

  const toggleHold = useHandler(async () => {
    if (call && isValidTelnyxCall(call.telnyxCall)) {
      try {
        setOnHold((prev) => !prev);
        await merchantRpcClient.toggleCallOnHold({
          customerCallId: call.callControlId,
          onHold: !onHold,
        });
        await call.telnyxCall?.toggleHold();
      } catch (error) {
        setOnHold((prev) => !prev);
      }
    }
  });

  const toggleRecord = useHandler(async () => {
    if (call) {
      try {
        setRecording((prev) => !prev);
        await merchantRpcClient.toggleCallBeingRecorded({
          customerCallId: call.callControlId,
          beingRecorded: !recording,
        });
      } catch (error) {
        setRecording((prev) => !prev);
      }
    }
  });

  const initializeCall = useHandler(
    (
      customer: CustomerJson,
      customerNumber: string,
      callControlId: string,
      direction: CallDirection,
      state: CallState,
      telnyxCall?: TelnyxCall,
    ): Call => {
      return {
        state,
        callControlId,
        direction,
        startTime: new Date(Date.now()),
        remoteCallerNumber: customerNumber,
        agents: [],
        customer,
        telnyxCall,
      };
    },
  );

  const handleTeamEvent = useHandler(
    async (message: FrontendTeamNoticeEvent) => {
      switch (message.type) {
        case TeamNoticeEventType.CALL_CONNECTED: {
          if (message.direction === CallDirection.OUTBOUND) {
            if (call && call.state === CallState.RINGING) {
              const callConnectedToUs =
                message.customerNumber === call.remoteCallerNumber;
              if (callConnectedToUs) {
                // We only have the correct callControlId after we connect for outbound calls
                const updatedCall = {
                  ...call,
                  callControlId: message.customerCallControlId,
                  state: CallState.ACTIVE,
                  agents: [agent],
                };
                setCall(updatedCall);
              }
            }
          } else if (call) {
            const callConnectedToUs = message.agentId === user._id;
            const isOurCall =
              message.customerNumber === call.remoteCallerNumber;

            if (call.state === CallState.CONNECTING) {
              if (callConnectedToUs && isOurCall) {
                const updatedCall = {
                  ...call,
                  state: CallState.ACTIVE,
                  agents: [agent],
                };
                setCall(updatedCall);
              } else if (isOurCall) {
                // Someone else answered while we were connecting
                endCall();
              }
            } else if (call.state === CallState.RINGING && isOurCall) {
              // Someone else answered while we were still ringing
              endCall();
              rejectCall();
            }
          }

          break;
        }
        case TeamNoticeEventType.CALL_HANGUP: {
          if (call?.remoteCallerNumber === message.customerNumber) {
            endCall();
          }

          break;
        }
        case TeamNoticeEventType.END_RINGING: {
          if (
            call?.state === CallState.RINGING &&
            message.customerCallControlId === call?.callControlId
          ) {
            endCall();
            rejectCall();
          }

          break;
        }
        // No default
      }
    },
  );

  const handleUserEvent = useHandler(async (message: Notification) => {
    if (message.type === NotificationType.INCOMING_CALL && call === undefined) {
      show();
      const customer = await getCustomer(merchantClient, {
        id: message.customerId,
      });
      const newCall = initializeCall(
        customer,
        message.from_number,
        message.call_control_id,
        CallDirection.INBOUND,
        CallState.RINGING,
      );
      setCall(newCall);

      // Set up automatic rejection timeout (30 seconds)
      ringTimeoutRef.current = setTimeout(() => {
        setCall((currentCall) => {
          // Only reject if the call is still in RINGING state and matches the call control ID
          if (
            currentCall &&
            currentCall.state === CallState.RINGING &&
            currentCall.callControlId === message.call_control_id
          ) {
            return undefined;
          }
          return currentCall;
        });
        ringTimeoutRef.current = null;
      }, 30_000); // 30 seconds timeout
    }
  });

  useEffect(() => {
    const teamUnsubscribe = eventServer.subscribe({
      topic: MerchantAppTopic.TEAM,
      callback: handleTeamEvent,
    });
    const unsubscribe = eventServer.subscribe({
      topic: MerchantAppTopic.USER_NOTIFICATIONS,
      callback: handleUserEvent,
    });
    return () => {
      teamUnsubscribe();
      unsubscribe();
    };
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventServer]);

  useEffect(() => {
    if (call && call.telnyxCall && !isValidTelnyxCall(call.telnyxCall)) {
      endCall();
    }
  }, [call, endCall, isValidTelnyxCall, call?.telnyxCall?.state]);

  useLoad(async () => {
    if (!telnyxClientRef.current) {
      await createTelnyxClient();
    }

    return () => {
      if (telnyxClientRef.current) {
        telnyxClientRef.current.disconnect().catch(() => {});
        detachListeners(telnyxClientRef.current);
        telnyxClientRef.current = null;
      }
      if (ringTimeoutRef.current) {
        clearTimeout(ringTimeoutRef.current);
        ringTimeoutRef.current = null;
      }
    };
  }, [createTelnyxClient, detachListeners]);

  const state: VoiceState = {
    call,
    status: userCallAvailability.availabilityStatus,
    setStatus: userCallAvailability.setAvailabilityStatus,
    performCall,
    rejectCall,
    endCall,
    answerCall,
    muted,
    toggleMute,
    deafened,
    toggleDeafen,
    onHold,
    toggleHold,
    recording,
    toggleRecord,
  };

  return (
    <Flex className={voiceWidgetCss.phone}>
      <VoiceContext.Provider value={state}>
        {isVisible && (
          <PhoneWidget
            merchantPhoneNumber={phoneNumber.number}
            status={userCallAvailability.availabilityStatus}
          />
        )}
      </VoiceContext.Provider>
      {call && <Audio stream={call.telnyxCall?.remoteStream} />}
    </Flex>
  );
});
